import "./instrument";
import { createRoot } from "react-dom/client";
import * as Sentry from "@sentry/react";
import App from "./App.tsx";
import { initDatadog } from "./lib/datadog";
import { safeSessionStorage } from "./lib/safeStorage";
import "./index.css";

initDatadog();

// Listen for chunk load errors or dynamic import failures and reload the page
const CHUNK_RELOAD_KEY = "chunk-load-reload-timestamp";

function handleChunkLoadError(errorString: string) {
  if (
    errorString.includes("Failed to fetch dynamically imported module") ||
    errorString.includes("Importing a module script failed") ||
    errorString.includes("error loading dynamically imported module")
  ) {
    const now = Date.now();
    const lastReload = safeSessionStorage.getItem(CHUNK_RELOAD_KEY);

    if (!lastReload || now - parseInt(lastReload, 10) > 10000) {
      safeSessionStorage.setItem(CHUNK_RELOAD_KEY, String(now));
      console.warn("Dynamic import / chunk load error detected. Reloading page to fetch the latest bundle...");
      window.location.reload();
    } else {
      console.error("Chunk load error detected, but reload was throttled to prevent an infinite loop.");
    }
  }
}

window.addEventListener("error", (event) => {
  handleChunkLoadError(event.message || "");
}, true);

window.addEventListener("unhandledrejection", (event) => {
  const reason = event.reason;
  const errorString = reason?.message || String(reason || "");
  handleChunkLoadError(errorString);
});

// ---------------------------------------------------------------------------
// DOM mutation safety patch
// Browser extensions (translation tools, ad blockers, Grammarly, etc.) can
// reparent or remove DOM nodes that React still references. When React later
// tries to removeChild / insertBefore on those nodes, it throws a
// NotFoundError ("The node to be removed is not a child of this node").
// We patch these two methods to silently recover instead of crashing the
// entire React tree.  Fixes Sentry PORTFOLIO-7.
// ---------------------------------------------------------------------------
if (typeof Node !== "undefined") {
  const origRemoveChild = Node.prototype.removeChild;

  Node.prototype.removeChild = function <T extends Node>(child: T): T {
    if (child.parentNode !== this) {
      console.warn(
        "[DOM patch] removeChild: node is not a child of this parent — suppressed.",
      );
      return child;
    }
    return origRemoveChild.call(this, child) as T;
  };

  const origInsertBefore = Node.prototype.insertBefore;

  Node.prototype.insertBefore = function <T extends Node>(
    newNode: T,
    refNode: Node | null,
  ): T {
    if (refNode && refNode.parentNode !== this) {
      console.warn(
        "[DOM patch] insertBefore: reference node is not a child of this parent — suppressed.",
      );
      return newNode;
    }
    return origInsertBefore.call(this, newNode, refNode) as T;
  };
}

createRoot(document.getElementById("root")!).render(
  <Sentry.ErrorBoundary fallback={<p>Something went wrong</p>} showDialog>
    <App />
  </Sentry.ErrorBoundary>
);
